import './index.scss';

import React, {useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {
	MAX_CATEGORY_NAME_LENGTH,
	MAX_LINK_NAME_LENGTH,
	MAX_LINK_URL_LENGTH,
	resolveLinkProfileIdsForSave
} from '../../tools';
import {resolveInitialProfileSelection} from '../../profileSelection';
import Modal from '../Modal';
import ProfileMultiSelect from '../ProfileMultiSelect';
import ImageGallery from '../ImageGallery';
import {useSuggestions} from "../../containers/Tools/useSuggestions";
import {useStorageImages} from "../../hooks/useStorageImages";
import {StateContext} from "../../App";
import {
	getLinkImageDisplayUrl,
	isFirebaseStorageImageUrl,
	resolveLinkImageForForm
} from '../../favicon';
import {BUNDLED_DEFAULT_LINK_IMAGE} from '../../firebaseTools';

const FAVICON_RESOLVE_DEBOUNCE_MS = 350;

const EditLink = (props) => {
	const {links, categories, profiles, link, activeProfile, onClose, onSave, onRemove} = props;
	const {addError} = useContext(StateContext);

	const [linkData, setLinkData] = useState({
		id: link.id,
		name: link.name,
		url: link.url,
		image: link.image,
		category: link.category,
		categoryId: link.categoryId,
	});
	const [selectedProfileIds, setSelectedProfileIds] = useState(
		() => resolveInitialProfileSelection(link?.profileIds, profiles, activeProfile)
	);
	const [profileSelectionChanged, setProfileSelectionChanged] = useState(false);

	const [imageMode, setImageMode] = useState('current');
	const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
	const [imageManuallyChosen, setImageManuallyChosen] = useState(false);
	const [isResolvingFavicon, setIsResolvingFavicon] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const {storageImages, loadingImages} = useStorageImages(addError, imageMode === 'select', links);

	useEffect(() => {
		if (!(linkData.image instanceof File)) {
			setImagePreviewUrl(null);
			return undefined;
		}
		const url = URL.createObjectURL(linkData.image);
		setImagePreviewUrl(url);
		return () => {
			URL.revokeObjectURL?.(url);
		};
	}, [linkData.image]);

	const uploadPreviewSrc = imagePreviewUrl || getLinkImageDisplayUrl(linkData.image);
	const currentPreviewSrc = linkData.url === link.url
		? getLinkImageDisplayUrl(link.image)
		: (uploadPreviewSrc || getLinkImageDisplayUrl(linkData.image) || BUNDLED_DEFAULT_LINK_IMAGE);
	const showCurrentPreview = imageMode === 'current' && !isResolvingFavicon;
	const showCurrentLoading = imageMode === 'current' && isResolvingFavicon;

	useEffect(() => {
		if (imageManuallyChosen || !linkData.url || linkData.url === link.url) {
			setIsResolvingFavicon(false);
			return undefined;
		}

		let cancelled = false;
		const requestUrl = linkData.url;
		setIsResolvingFavicon(true);

		const timeoutId = setTimeout(() => {
			(async () => {
				try {
					const file = await resolveLinkImageForForm(requestUrl);
					if (cancelled) return;
					setLinkData((prev) => {
						if (prev.url !== requestUrl) return prev;
						if (prev.url === link.url) {
							return {...prev, image: link.image};
						}
						return {
							...prev,
							image: file ?? BUNDLED_DEFAULT_LINK_IMAGE
						};
					});
				} finally {
					if (!cancelled) {
						setIsResolvingFavicon(false);
					}
				}
			})();
		}, FAVICON_RESOLVE_DEBOUNCE_MS);

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
		};
	}, [link.url, link.image, linkData.url, imageManuallyChosen]);

	const {
		suggestions,
		showSuggestions,
		updateSuggestions,
		hideSuggestions
	} = useSuggestions(categories, cat => cat.category);

	const handleChange = (e) => {
		const {name, value} = e.target;
		setLinkData(prev => {
			const next = {...prev, [name]: value};
			if (name === 'url' && !imageManuallyChosen) {
				if (value === link.url) {
					next.image = link.image;
				} else {
					next.image = null;
				}
			}
			return next;
		});

		if (name === 'category') {
			updateSuggestions(value);
		}
	};

	const handleSuggestionClick = (suggestion) => {
		setLinkData(prev => ({...prev, category: suggestion}));
		hideSuggestions();
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		if (isSaving) return;
		const profileIds = resolveLinkProfileIdsForSave({
			link,
			categories,
			profiles,
			selectedProfileIds,
			profileSelectionChanged
		});

		const updatedLinkData = {
			...linkData,
			profileIds,
		};

		setIsSaving(true);
		onSave({...link, ...updatedLinkData});
	};

	return (
		<Modal onClose={onClose}>
			<form onSubmit={handleSubmit} className="edit-link-form">
				<h2>Edit Link</h2>
				<div className="form-group">
					<div className="form-group__field">
						<label htmlFor="name">Name:</label>
						<input
							type="text"
							id="name"
							name="name"
							value={linkData.name}
							onChange={handleChange}
							required
							maxLength={MAX_LINK_NAME_LENGTH}
						/>
					</div>
				</div>
				<div className="form-group">
					<div className="form-group__field">
						<label htmlFor="category">Category:</label>
						<input
							type="text"
							id="category"
							name="category"
							value={linkData.category}
							onChange={handleChange}
							required
							autoComplete="off"
							maxLength={MAX_CATEGORY_NAME_LENGTH}
						/>
					</div>
					{showSuggestions && (
						<ul className="category-suggestions">
							{suggestions.map((suggestion, index) => (
								<li key={`${suggestion}-${index}`}>
									<button
										type="button"
										className="category-suggestions__item"
										onClick={() => handleSuggestionClick(suggestion)}
									>
										{suggestion}
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
				<div className="form-group">
					<div className="form-group__field">
						<label htmlFor="url">URL:</label>
						<input
							type="url"
							id="url"
							name="url"
							value={linkData.url}
							onChange={handleChange}
							required
							maxLength={MAX_LINK_URL_LENGTH}
						/>
					</div>
				</div>
				<div className="form-group form-group--profile-picker">
					<div className="form-group__field">
						<label htmlFor="profiles">Profiles:</label>
						<ProfileMultiSelect
							id="profiles"
							profiles={profiles}
							selectedProfileIds={selectedProfileIds}
							onChange={(profileIds) => {
								setSelectedProfileIds(profileIds);
								setProfileSelectionChanged(true);
							}}
						/>
					</div>
				</div>
				<div className="form-group">
					<fieldset className="form-group__field form-group__field--align-start edit-link-form__image-field">
						<legend className="edit-link-form__image-legend">Image:</legend>
						<div className="image-selection">
							<div className="image-selection__tabs">
								<button
									type="button"
									className={`image-selection__tab ${imageMode === 'current' ? 'image-selection__tab--active' : ''}`}
									onClick={() => {
										setImageMode('current');
										setImageManuallyChosen(false);
									}}
								>
									Current
								</button>
								<button
									type="button"
									className={`image-selection__tab ${imageMode === 'upload' ? 'image-selection__tab--active' : ''}`}
									onClick={() => {
										setImageMode('upload');
										setImageManuallyChosen(false);
										setLinkData(prev => ({...prev, image: null}));
									}}
								>
									Upload New
								</button>
								<button
									type="button"
									className={`image-selection__tab ${imageMode === 'select' ? 'image-selection__tab--active' : ''}`}
									onClick={() => {
										setImageMode('select');
										setImageManuallyChosen(true);
										setLinkData(prev => ({...prev, image: null}));
									}}
									disabled={loadingImages}
								>
									{loadingImages ? 'Loading...' : 'Select Existing'}
								</button>
							</div>
							<div className="image-selection__body">
								{imageMode === 'current' && (
									<div
										className={`image-selection__preview${
											showCurrentLoading ? ' image-selection__upload-tile--loading' : ''
										}`}
										aria-busy={showCurrentLoading}
									>
										{showCurrentLoading ? (
											<span className="image-selection__upload-tile__spinner" aria-hidden="true" />
										) : showCurrentPreview ? (
											<img src={currentPreviewSrc} alt="Current" />
										) : null}
									</div>
								)}
							{imageMode === 'upload' && (
								<label
									className={`image-selection__upload-tile${
										uploadPreviewSrc && !isResolvingFavicon ? ' image-selection__upload-tile--filled' : ''
									}${isResolvingFavicon ? ' image-selection__upload-tile--loading' : ''}`}
									aria-busy={isResolvingFavicon}
								>
									<input
										type="file"
										name="image"
										aria-label="Image"
										onChange={(e) => {
											setImageManuallyChosen(true);
											setIsResolvingFavicon(false);
											setLinkData(prev => ({
												...prev,
												image: e.target.files[0]
											}));
										}}
									/>
									{isResolvingFavicon ? (
										<span className="image-selection__upload-tile__spinner" aria-hidden="true" />
									) : uploadPreviewSrc ? (
										<img src={uploadPreviewSrc} alt="Selected image preview" />
									) : (
										<span className="image-selection__upload-tile__placeholder" aria-hidden="true">
											+
										</span>
									)}
								</label>
							)}
								{imageMode === 'select' && (
									<ImageGallery
										images={storageImages}
										selectedImage={typeof linkData.image === 'string' && isFirebaseStorageImageUrl(linkData.image) ? linkData.image : null}
										onSelect={(url) => {
											setImageManuallyChosen(true);
											setLinkData(prev => ({...prev, image: url}));
										}}
									/>
								)}
							</div>
						</div>
					</fieldset>
				</div>
				<div className="form-actions">
					<button type="submit" className="btn-save" disabled={isSaving} aria-busy={isSaving}>
						{isSaving ? 'Saving\u2026' : 'Save'}
					</button>
					<button type="button" className="btn-remove" onClick={() => onRemove(link)} disabled={isSaving}>
						Remove
					</button>
					<button type="button" className="btn-cancel" onClick={onClose} disabled={isSaving}>Cancel</button>
				</div>
			</form>
		</Modal>
	);
};

EditLink.propTypes = {
	links: PropTypes.array.isRequired,
	categories: PropTypes.array.isRequired,
	profiles: PropTypes.array.isRequired,
	activeProfile: PropTypes.shape({
		id: PropTypes.string
	}),
	link: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		url: PropTypes.string.isRequired,
		image: PropTypes.string.isRequired,
		category: PropTypes.string.isRequired,
		categoryId: PropTypes.string,
		profileIds: PropTypes.array
	}).isRequired,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired
};

export default EditLink;
